import { Button, Combobox, FormField } from "@a2z/ui";

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { useDialogDismiss } from "@/lib/hooks/useDialogDismiss";

type SellerOption = {
  id: string;
  name: string;
};

type StoreFiltersProps = {
  basePath: string;
  limit: number;
  initialSellerId?: string;
  sellerOptions: SellerOption[];
};

export function StoreFilters({
  basePath,
  limit,
  initialSellerId,
  sellerOptions,
}: StoreFiltersProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const [isOpen, setIsOpen] = useState(false);
  useDialogDismiss(isOpen, () => setIsOpen(false));
  const [sellerId, setSellerId] = useState(initialSellerId ?? "");
  const hasActiveFilters = Boolean((sellerId || initialSellerId || "").trim());
  const hasPendingChanges = (sellerId ?? "") !== (initialSellerId ?? "");

  const options = useMemo(
    () => [
      { label: t(keys.admin.storesFilterAllSellers), value: "" },
      ...sellerOptions.map((seller) => ({
        label: seller.name,
        value: seller.id,
      })),
    ],
    [sellerOptions, t],
  );

  function buildQuery(nextSellerId?: string) {
    const query = new URLSearchParams();
    query.set("limit", String(limit));
    query.set("offset", "0");
    if (nextSellerId) {
      query.set("sellerId", nextSellerId);
    }
    return query.toString();
  }

  function applyFilters() {
    navigate(`${basePath}?${buildQuery(sellerId || undefined)}`);
    setIsOpen(false);
  }

  function clearFilters() {
    setSellerId("");
    navigate(basePath);
    setIsOpen(false);
  }

  return (
    <>
      <Button
        type="button"
        onClick={() => setIsOpen(true)}
        variant="outline" className="inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold text-[color:var(--text-muted)]"
      >
        {t(keys.admin.storesFilterTitle)}
        {hasActiveFilters ? (
          <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[color:var(--primary-100)] px-2 text-[13px] font-semibold text-[color:var(--primary-600)]">
            1
          </span>
        ) : null}
      </Button>

      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pt-8 pb-[max(2rem,env(safe-area-inset-bottom))] sm:items-center"
          role="dialog"
          aria-modal="true"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="max-h-full w-full max-w-lg overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
                {t(keys.admin.storesFilterTitle)}
              </h2>
              <Button variant="ghost"
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-sm font-semibold text-[color:var(--text-muted)]"
              >
                {t(keys.common.hide)}
              </Button>
            </div>

            <div className="mt-5 grid gap-4">
              <FormField
                label={t(keys.admin.storesFilterSellerLabel)}
                name="sellerId"
              >
                <Combobox
                  value={sellerId}
                  options={options}
                  onChange={(value) => setSellerId(value ?? "")}
                  aria-label={t(keys.admin.storesFilterSellerLabel)}
                />
              </FormField>
            </div>

            <div className="mt-6 flex flex-col gap-3">
              <Button
                type="button"
                onClick={applyFilters}
                disabled={!hasPendingChanges}
                variant="primary" className="h-11 w-full rounded-full text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t(keys.common.applyFilters)}
              </Button>
              <Button
                type="button"
                onClick={clearFilters}
                disabled={!hasActiveFilters}
                variant="outline" className="h-11 w-full rounded-full text-sm font-semibold text-[color:var(--text-muted)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {t(keys.common.clearFilters)}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
